import { ColorPicker } from 'modules/Editor/components/ColorPicker';
import { BorderRadiusPopover, BorderWidthPopover } from 'modules/common/components/Border';
import { ToolbarDivider } from 'modules/common/components/Toolbar/ToolbarDivider';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { BorderColor } from 'widgets/TextBasedWidgets/TableWidget/toolbar/Color';
import { useWidget } from 'widgets/sdk';

import { FormWidgetData } from '../FormWidget.types';
import EditFieldOption from './EditFieldOption/EditFieldOption';

export const InputToolbar = () => (
  <>
    <EditFieldOption />
    <ToolbarDivider />
    <BackgroundColor />
    <FormBorderColor />
    <ToolbarDivider />
    <BorderWidth />
    <BorderRadius />
  </>
);

const FormBorderColor = memo(() => {
  const {
    style: { borderColor },
    updateWidget,
  } = useWidget<FormWidgetData>();

  const onSetBorderColor = useCallback(
    (color: string) => {
      updateWidget({ style: { borderColor: color } });
    },
    [updateWidget],
  );

  return <BorderColor color={borderColor} onChange={onSetBorderColor} />;
});
FormBorderColor.displayName = 'FormBorderColor';

const BorderWidth = memo(() => {
  const {
    style: { borderWidth },
    updateWidget,
  } = useWidget<FormWidgetData>();

  const onUpdateBorderWidth = useCallback(
    (borderWidth: number) => {
      updateWidget({ style: { borderWidth } });
    },
    [updateWidget],
  );

  return <BorderWidthPopover value={borderWidth} onChange={onUpdateBorderWidth} />;
});
BorderWidth.displayName = 'FormBorderWidth';

const BorderRadius = memo(() => {
  const {
    style: { borderRadius },
    updateWidget,
  } = useWidget<FormWidgetData>();

  const onUpdateBorderRadius = useCallback(
    (borderRadius: number) => {
      updateWidget({ style: { borderRadius } });
    },
    [updateWidget],
  );

  return <BorderRadiusPopover value={borderRadius} onChange={onUpdateBorderRadius} suffix='' max={20} />;
});
BorderRadius.displayName = 'FormBorderRadius';

const BackgroundColor = memo(() => {
  const {
    style: { backgroundColor },
    updateWidget,
  } = useWidget<FormWidgetData>();
  const { t } = useTranslation('editor_widget_toolbar', { useSuspense: false, keyPrefix: 'formToolbar' });

  const onUpdateBackgroundColor = useCallback(
    (backgroundColor: string) => {
      updateWidget({ style: { backgroundColor } });
    },
    [updateWidget],
  );

  return (
    <ColorPicker
      color={backgroundColor}
      label={t('backgroundColor')}
      onChange={onUpdateBackgroundColor}
      showNoColorOption={false}
      showNoColorChecker
    />
  );
});
BackgroundColor.displayName = 'FormBackgroundColor';
